/**
 * Numeric metric tile with optional sub + tone + accent border.
 * Pattern from Overview tab `.tg-cell`.
 */
import type { BadgeClass } from '@/lib/store/drones';
import { cn } from '@/lib/utils';

export function MetricCell({
  label,
  value,
  sub,
  tone,
  mono,
  accent,
  accentColor,
  className,
}: {
  label: string;
  value: string;
  sub?: string | null;
  tone?: BadgeClass;
  mono?: boolean;
  accent?: boolean;
  accentColor?: 'blue' | 'green' | 'amber' | 'red';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) px-2.5 py-2 text-center min-w-0',
        accent && tone === 'crit' && 'border-t-2 border-t-(--color-red)',
        accent && tone === 'warn' && 'border-t-2 border-t-(--color-amber)',
        accent && tone === 'ok' && 'border-t-2 border-t-(--color-green)',
        accentColor === 'blue' && 'border-t-2 border-t-(--color-blue)',
        accentColor === 'green' && 'border-t-2 border-t-(--color-green)',
        accentColor === 'amber' && 'border-t-2 border-t-(--color-amber)',
        accentColor === 'red' && 'border-t-2 border-t-(--color-red)',
        className,
      )}
    >
      {/* These tiles sit in a 3-column grid inside a ~280 px panel, so a
          value like "OF_LOITER" is wider than its cell. Grid items default to
          min-width:auto and would push past the border instead of wrapping —
          `min-w-0` + `break-words` on the tile keeps every value inside. */}
      <div
        className={cn(
          'text-[1em] font-bold break-words',
          mono && 'font-mono',
          accentColor === 'blue' && 'text-(--color-blue) font-mono',
          tone === 'ok' && 'text-(--color-green)',
          tone === 'warn' && 'text-(--color-amber)',
          tone === 'crit' && 'text-(--color-red)',
        )}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[0.692em] font-mono text-(--color-text3) mt-0.5 break-words">
          {sub}
        </div>
      )}
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mt-1 break-words">
        {label}
      </div>
    </div>
  );
}

export function MetricRow3({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-3 gap-1.5', className)}>{children}</div>;
}

export function MetricRow2({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-2 gap-1.5', className)}>{children}</div>;
}
